import { useMutation, type UseMutationOptions } from '@tanstack/react-query';

import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';

import { vestigasApi } from '~/services/kyClient';

import { type CustomFieldOption } from './useQueryCustomFieldOption';

type CustomFieldResponse = Record<string, unknown>;

type CustomFieldOptionItem = Omit<CustomFieldOption, 'id'>;

type CustomFieldOptionsArguments = {
  customFieldId: UUID;
  customFieldOptions: CustomFieldOptionItem[];
};

/**
 * Updates all options of a custom field.
 *
 * @param customFieldId - A uuid containing the custom field to be updated.
 * @param customFieldOptions - An array of custom field options to be updated.
 * @returns A promise that resolves with the API response.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Custom-Field/operation/update_custom_field_options_custom_field__custom_field_id__options_put
 */
export const updateCustomFieldOptions = async (
  customFieldId: UUID,
  customFieldOptions: CustomFieldOptionItem[],
) => {
  const response = await vestigasApi
    .put(ENDPOINT.CUSTOM_FIELD.UPDATE_OPTIONS(customFieldId), {
      json: {
        options: customFieldOptions,
      },
    })
    .json<CustomFieldResponse>();

  return response;
};

/**
 * Custom hook for updating all options of a custom field using mutations.
 *
 * @returns An object containing the mutation function and other properties.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Custom-Field/operation/update_custom_field_options_custom_field__custom_field_id__options_put
 */
export const useMutationUpdateCustomFieldOptions = (
  options?: UseMutationOptions,
) => {
  return useMutation({
    mutationFn: async ({
      customFieldId,
      customFieldOptions,
    }: CustomFieldOptionsArguments) =>
      updateCustomFieldOptions(customFieldId, customFieldOptions),
    ...options,
  });
};
